// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useContext, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { sopDetailPath } from '../../services/sopPath';
import { ActionableNotification, Button } from '@carbon/react';
import { ArrowLeft, ChevronDown } from '@carbon/icons-react';
import { useAlarm } from '../../hooks/useAlarms';
import { useActiveSite } from '../../hooks/useActiveSite';
import { useAuth } from '../../hooks/useAuth';
import { useSiteTime } from '../../hooks/useSiteTime';
import { AuthContext } from '../../contexts/AuthContext';
import { sameAccount } from '../../components/shell/alarms/AlarmOwnerLine';
import { useFlagOn } from '../../hooks/useFlagOn';
import { useShellStore } from '../../stores/shellStore';
import { SevTag, Loading, DxTag } from '../../components/shell/ScreenHelpers';
import AlarmTelemetry from '../../components/shell/AlarmTelemetry';
import WorkOrdersPanel, { workOrderTone } from '../../components/shell/WorkOrdersPanel';
import { useWorkOrderStatus } from '../../hooks/useWorkOrderMutation';
import { useRaisedWorkOrder } from '../../hooks/useAlarmWorkOrderLinks';
import { useWorkOrderLookup } from '../../hooks/useWorkOrders';
import { shownStatusLabel } from '../../types/alarmFsm';
import { workOrderPath } from '../../services/workOrderPath';
import InlineEquipmentView from '../../components/equipment/InlineEquipmentView';
import AssetLineupPanel from '../../components/equipment/AssetLineupPanel';
import { locationShort } from '../../utils/location';
import AlarmBriefCard from './AlarmBriefCard';
import AlarmNextStepCard, { workOrderCreateBlocker } from './AlarmNextStepCard';
import type { AlarmNextStepCardHandle } from './AlarmNextStepCard';
import { StaleDataNotice } from '../../components/common/StaleDataNotice';
import AlarmMetaGrid from './AlarmMetaGrid';
import './AlarmDetail.css';

export default function AlarmDetail() {
  const { alarmCode } = useParams();
  const [searchParams] = useSearchParams();
  const { siteId: activeSiteId } = useActiveSite();
  const { sites } = useAuth();
  const alarmLifecycleEnabled = useFlagOn('alarmLifecycleEnabled');
  const responseProcedureEnabled = useFlagOn('alarmResponseProcedureEnabled');
  const siteId = searchParams.get('site') ?? activeSiteId;
  const equipmentId = searchParams.get('equipment') ?? undefined;
  const alarmId = searchParams.get('alarm_id') ?? undefined;
  const navigate = useNavigate();
  const location = useLocation();
  const backTo = (location.state as { backTo?: string } | null)?.backTo;
  const {
    alarm,
    loading,
    isError,
    error,
    refetch: refetchAlarm,
  } = useAlarm(alarmCode, siteId, equipmentId, alarmId);
  const setPageAlarmContext = useShellStore(s => s.setPageAlarmContext);
  // Hooks run before the early returns below, so the lookup takes a stand-in
  // alarm while this one is still loading.
  const raisedWorkOrder = useRaisedWorkOrder(alarm ?? { alarmId: undefined, siteId: '' });
  // The order's own status comes from the WOMS: AEM carries only the id.
  const { workOrder: linkedOrder } = useWorkOrderLookup(
    raisedWorkOrder?.siteId ?? null,
    raisedWorkOrder?.id ?? null,
    Boolean(raisedWorkOrder)
  );
  // Header chip: the order's status once one exists, else the AEM state (AB#34484).
  const statusLabel = shownStatusLabel(alarm?.status, linkedOrder);
  // The Work orders card's create prompt opens the Next step card's own dialog.
  const nextStep = useRef<AlarmNextStepCardHandle>(null);
  // Without a remembered link, useRaisedWorkOrder carries AEM's work_order_id as
  // a stand-in reference; the WOMS read supplies the human one.
  const orderReference = linkedOrder?.reference ?? raisedWorkOrder?.reference;
  const createBlocked = workOrderCreateBlocker(
    'open_loop',
    alarm?.status,
    raisedWorkOrder && orderReference ? { reference: orderReference } : raisedWorkOrder
  );
  // Start work / Mark complete are WOMS writes with no alarm event behind them
  // (single lifecycle); completing re-reads the alarm so its tracker follows.
  const orderMutation = useWorkOrderStatus();
  const [orderPending, setOrderPending] = useState<'start' | 'complete' | null>(null);
  const [orderError, setOrderError] = useState<string | null>(null);
  function setOrderStatus(status: 'in_progress' | 'completed') {
    if (!raisedWorkOrder) return;
    setOrderError(null);
    setOrderPending(status === 'completed' ? 'complete' : 'start');
    orderMutation.mutate(
      { siteId: raisedWorkOrder.siteId ?? null, id: raisedWorkOrder.id, status },
      {
        onSuccess: () => {
          if (status === 'completed') refetchAlarm();
        },
        onError: error => setOrderError(error.message || 'The work order could not be updated.'),
        onSettled: () => setOrderPending(null),
      }
    );
  }
  const orderStatus = linkedOrder?.status;
  const startBlocked = !linkedOrder
    ? 'The work order could not be read.'
    : orderStatus === 'scheduled'
      ? undefined
      : orderStatus === 'in_progress'
        ? 'Work has already started.'
        : orderStatus === 'completed'
          ? 'This work order is complete.'
          : `This work order is ${linkedOrder.status_label.toLowerCase()}.`;
  const completeBlocked = !linkedOrder
    ? 'The work order could not be read.'
    : orderStatus === 'completed'
      ? 'This work order is already complete.'
      : orderStatus === 'canceled'
        ? 'This work order was canceled.'
        : undefined;
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  // Same identity handling as the owner row: "You" for the viewer, else the stored identifier.
  const myEmail = useContext(AuthContext)?.user?.email;
  const { formatIn } = useSiteTime();
  const closedAtText = alarm?.closed
    ? formatIn(alarm.siteId, alarm.closedAt, 'dateTime', '') || undefined
    : undefined;
  const closedByText = alarm?.closed
    ? sameAccount(alarm.closedBy, myEmail)
      ? 'You'
      : alarm.closedBy
    : undefined;
  const closedLine =
    closedByText && closedAtText
      ? `Closed by ${closedByText} on ${closedAtText}`
      : closedByText
        ? `Closed by ${closedByText}`
        : closedAtText
          ? `Closed on ${closedAtText}`
          : undefined;

  useEffect(() => {
    if (!alarm) return;
    const sops = alarm.sops ?? [];
    const nextCollapsed: Record<string, boolean> = {};
    sops.forEach((s, i) => {
      nextCollapsed[s.id] = i > 0;
    });
    setCollapsed(nextCollapsed);
  }, [alarm]);

  useEffect(() => {
    if (!alarm) return;
    setPageAlarmContext({
      id: alarm.id,
      alarm_id: alarm.alarmId,
      rule: alarm.ruleId,
      asset: alarm.asset,
      site: locationShort(alarm.location),
      ranked_causes:
        alarm.causes?.map(c => ({
          cause: c.c,
          rank: c.rank,
          confidence: c.conf,
          why: c.why,
        })) ?? [],
      bound_sops: alarm.sopIds,
      triage_summary: alarm.diagnosis,
      status: alarm.status,
      assigned_to: alarm.owner,
      acknowledged_by: alarm.acknowledgedBy,
      acknowledged_at: alarm.acknowledgedAt,
      equipment_type: alarm.group,
      rule_title: alarm.ruleTitle,
      category: alarm.category,
      priority: alarm.priority,
      control_layer: alarm.controlLayer,
      event_type: 'alarm',
    });
    return () => setPageAlarmContext(null);
  }, [alarm, sites, setPageAlarmContext]);

  if (loading) return <Loading />;
  // "Not found" only for a genuine empty result: a 5xx, 403 or network failure
  // is retryable and must say so.
  if (isError && !alarm) {
    return (
      <div className="hs-page">
        <ActionableNotification
          kind="error"
          lowContrast
          title="Couldn't load this alarm"
          subtitle={error ?? 'Request failed'}
          actionButtonLabel="Retry"
          onActionButtonClick={() => refetchAlarm()}
          hideCloseButton
        />
        <Button kind="ghost" onClick={() => navigate('/home/alarms')}>
          Back to alarms
        </Button>
      </div>
    );
  }
  if (!alarm) {
    return (
      <div className="hs-page">
        <p>Alarm not found.</p>
        <Button kind="ghost" onClick={() => navigate('/home/alarms')}>
          Back to alarms
        </Button>
      </div>
    );
  }

  const sevTagLabel =
    alarm.ruleUnavailable && alarm.sev === 'Unknown' ? 'Severity unavailable' : undefined;

  return (
    <div className="hs-page ald-page" data-testid="alarm-detail">
      <button
        type="button"
        className="ald-back"
        onClick={() => (backTo ? navigate(backTo) : navigate(-1))}
      >
        <ArrowLeft size={16} /> Back to list
      </button>

      {isError && (
        <StaleDataNotice what="this alarm" error={error} onRetry={() => refetchAlarm()} />
      )}
      {alarm.ruleUnavailable && (
        <StaleDataNotice
          title="Couldn't load this alarm's rule"
          subtitle={
            responseProcedureEnabled
              ? 'Severity, source and linked procedures may be incomplete.'
              : 'Severity and source may be incomplete.'
          }
          onRetry={() => refetchAlarm()}
        />
      )}

      <div className="ald-head">
        <div>
          <div className="hs-card-row" style={{ marginBottom: 8 }}>
            <SevTag sev={alarm.sev} label={sevTagLabel} />
            {statusLabel && <DxTag>{statusLabel}</DxTag>}
            {alarm.siteId && (
              <span className="ald-site">
                {sites.find(s => s.site_id === alarm.siteId)?.label ?? alarm.siteId}
              </span>
            )}
          </div>
          <h1 className="hs-title">{alarm.asset}</h1>
          <p className="ald-issue" data-testid="alarm-issue">
            {alarm.issue}
          </p>
          {closedLine && (
            <p className="ald-issue" data-testid="alarm-closed-by">
              {closedLine}
            </p>
          )}
        </div>
      </div>

      {/* Metadata — full width, above the two-column body */}
      <AlarmMetaGrid alarm={alarm} />

      <div className="ald-grid">
        <div className="ald-col-main">
          <AlarmBriefCard alarm={alarm} eventType="alarm" />

          {/* Response procedure */}
          {responseProcedureEnabled && (
            <section
              className="ald-card ald-proc"
              aria-labelledby="ald-proc-title"
              data-testid="alarm-procedure"
            >
              <div className="ald-proc-head">
                <div className="ald-proc-title" id="ald-proc-title">
                  Response procedure
                </div>
              </div>
              {(alarm.sops ?? []).length === 0 ? (
                <p className="ald-proc-empty">
                  {alarm.ruleUnavailable
                    ? "Couldn't load the linked procedures. Retry above."
                    : 'No response procedure linked.'}
                </p>
              ) : (
                (alarm.sops ?? []).map(sop => {
                  const failed = sop.status === 'failed';
                  const open = !collapsed[sop.id];
                  return (
                    <div key={sop.id} className="ald-proc-sop">
                      <div className="ald-proc-sop-head">
                        <div className="ald-proc-meta">
                          <Link to={sopDetailPath(sop.id, alarm.siteId)}>{sop.id}</Link>
                          {sop.title ? ` · ${sop.title}` : ''}
                          {sop.version ? ` · ${sop.version}` : ''}
                          {sop.labor ? ` · ${sop.labor}` : ''}
                        </div>
                        <button
                          type="button"
                          className="ald-proc-chevron"
                          aria-expanded={open}
                          aria-controls={`ald-proc-body-${sop.id}`}
                          aria-label={`${open ? 'Collapse' : 'Expand'} ${sop.title ?? sop.id}`}
                          onClick={() => setCollapsed(c => ({ ...c, [sop.id]: !c[sop.id] }))}
                        >
                          <ChevronDown size={16} />
                        </button>
                      </div>
                      <div
                        id={`ald-proc-body-${sop.id}`}
                        className="ald-proc-sop-body"
                        hidden={!open}
                      >
                        {failed ? (
                          <p className="ald-proc-unavailable-note">This SOP could not be loaded.</p>
                        ) : (
                          <ol className="ald-steps">
                            {sop.steps.map((s, i) => {
                              const num = String(i + 1).padStart(2, '0');
                              return (
                                <li key={i} data-testid="alarm-procedure-step">
                                  <span className="ald-step-num">{num}</span>
                                  {s.verb && <span className="ald-step-verb">{s.verb}</span>}
                                  <div className="ald-step-body">
                                    <div className="ald-step-text">{s.instruction}</div>
                                  </div>
                                </li>
                              );
                            })}
                          </ol>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </section>
          )}

          <AlarmTelemetry siteId={alarm.siteId} equipmentId={alarm.assetId} />

          {/* Audit trail */}
          {alarm.auditTrail.length > 0 && (
            <section className="ald-card">
              <div className="ald-card-title">Audit trail</div>
              <ul className="ald-trail">
                {alarm.auditTrail.map((e, i) => (
                  <li key={i}>
                    <span className="ald-trail-t">{e.t}</span>
                    <span className="ald-trail-who">{e.who}</span>
                    <span>{e.text}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        <div className="ald-col-side">
          {/* Operator actions, derived from AEM's state machine rather than a
              fixed ladder — see AlarmNextStepCard.tsx / types/alarmFsm.ts.
              Alarms are governed by open_loop (event_type = 'alarm').
              Flagged off until AEM supports the two-status lifecycle. */}
          {alarmLifecycleEnabled && (
            <AlarmNextStepCard
              ref={nextStep}
              alarm={alarm}
              siteId={alarm.siteId}
              onEventApplied={refetchAlarm}
              machine="open_loop"
            />
          )}

          <WorkOrdersPanel
            className="ald-card"
            onCreate={
              alarmLifecycleEnabled && alarm.alarmId && !createBlocked
                ? () => nextStep.current?.raiseWorkOrder()
                : undefined
            }
            createBlocked={
              alarmLifecycleEnabled && alarm.alarmId ? (createBlocked ?? undefined) : undefined
            }
            error={orderError}
            workOrders={
              raisedWorkOrder
                ? [
                    {
                      reference: orderReference ?? raisedWorkOrder.reference,
                      to: workOrderPath(raisedWorkOrder.siteId ?? '', raisedWorkOrder.id),
                      statusLabel: linkedOrder?.status_label,
                      statusTone: workOrderTone(linkedOrder?.status),
                      onStart: startBlocked ? undefined : () => setOrderStatus('in_progress'),
                      startBlocked,
                      onComplete: completeBlocked ? undefined : () => setOrderStatus('completed'),
                      completeBlocked,
                      pending: orderPending,
                    },
                  ]
                : []
            }
          />

          <InlineEquipmentView
            equipmentId={alarm.assetId ?? ''}
            siteId={alarm.siteId}
            heading="Power Flow"
            className="ald-card"
          />

          <AssetLineupPanel
            equipmentId={alarm.assetId ?? ''}
            siteId={alarm.siteId}
            className="ald-card"
          />
        </div>
      </div>
    </div>
  );
}
